import React, { useEffect, useId, useRef, useState } from 'react';
import { Lock, Pencil } from 'lucide-react';
import Button from '../Button';
import { COD_CURRENCIES } from '../../constants/shipments';
import EntityPicker from './EntityPicker';

// One row of the detail data card ("dt / dd") with inline editing.
// kind: text | textarea | date | select | entity | cod. `locked` = the field was edited by hand (manualFields).
// onSave(patch) must return a promise (rejects with an Error -> its message is shown under the editor).
const EditableField = ({
  label, field, kind = 'text', value, display, options, entityKind, current, locked = false, editable = false,
  onSave, onUnlock, maxLength = 200, codCurrency = 'CZK',
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [currency, setCurrency] = useState(codCurrency);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputId = useId();
  const pencilRef = useRef(null);
  const formRef = useRef(null);
  const wasEditing = useRef(false);

  // focus moves into the editor and comes back to the pencil button when it closes
  useEffect(() => {
    if (editing) formRef.current?.querySelector('input, select, textarea')?.focus();
    else if (wasEditing.current) pencilRef.current?.focus();
    wasEditing.current = editing;
  }, [editing]);

  const start = () => {
    setDraft(value == null ? '' : String(value));
    setCurrency(codCurrency || 'CZK');
    setError('');
    setEditing(true);
  };

  const buildPatch = () => {
    const text = String(draft).trim();
    if (kind === 'cod') {
      if (text === '') return { codAmount: null, codCurrency: null };
      const n = Number(text.replace(',', '.'));
      if (!Number.isFinite(n) || n < 0) throw new Error('Zadejte částku jako nezáporné číslo.');
      return { codAmount: n, codCurrency: currency };
    }
    if (kind === 'entity') return { [field]: draft ? Number(draft) : null };
    return { [field]: text === '' ? null : text };
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await onSave(buildPatch());
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const unlock = async () => {
    setBusy(true);
    setError('');
    try { await onUnlock(field); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const control = () => {
    const common = { id: inputId, value: draft, 'aria-invalid': error ? 'true' : undefined, 'aria-describedby': error ? `${inputId}-err` : undefined };
    if (kind === 'textarea') return <textarea className="shp-textarea" rows={3} maxLength={1000} onChange={(e) => setDraft(e.target.value)} {...common} />;
    if (kind === 'date') return <input className="shp-input" type="date" onChange={(e) => setDraft(e.target.value)} {...common} />;
    if (kind === 'select') {
      return (
        <select className="shp-select" onChange={(e) => setDraft(e.target.value)} {...common}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (kind === 'cod') {
      return (
        <div className="shp-range">
          <input className="shp-input" inputMode="decimal" onChange={(e) => setDraft(e.target.value)} aria-label="Částka dobírky" {...common} />
          <select className="shp-select" value={currency} onChange={(e) => setCurrency(e.target.value)} aria-label="Měna dobírky">
            {COD_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      );
    }
    return <input className="shp-input" type="text" maxLength={maxLength} autoComplete="off" onChange={(e) => setDraft(e.target.value)} {...common} />;
  };

  return (
    <div className="shp-dl__row">
      <dt>{editing && kind !== 'entity' ? <label htmlFor={inputId}>{label}</label> : label}</dt>
      <dd>
        {editing ? (
          <form ref={formRef} className="shp-edit" onSubmit={submit} noValidate>
            {kind === 'entity'
              ? <EntityPicker kind={entityKind} label={label} value={draft} current={current} onChange={(id) => setDraft(id)} error={error} />
              : control()}
            {error && kind !== 'entity' && <p id={`${inputId}-err`} role="alert" className="shp-error" style={{ margin: 0 }}>{error}</p>}
            <div className="shp-edit__btns">
              <Button type="submit" loading={busy}>Uložit</Button>
              <Button variant="secondary" disabled={busy} onClick={() => setEditing(false)}>Zrušit</Button>
            </div>
          </form>
        ) : (
          <div className="shp-dl__view">
            <div className="shp-dl__value shp-wrap" dir="auto">
              {value == null || value === '' || !display ? <span className="shp-muted">–<span className="shp-sr">nevyplněno</span></span> : display}
            </div>
            <span className="shp-dl__tools">
              {locked && <span className="shp-tag shp-tag--info" title="Pole bylo upraveno ručně, synchronizace ho nepřepíše."><Lock size={12} aria-hidden="true" /> ručně</span>}
              {locked && editable && (
                <button type="button" className="shp-textbtn" onClick={unlock} disabled={busy} aria-label={`Odemknout pole: ${label}`}>Odemknout</button>
              )}
              {editable && (
                <button ref={pencilRef} type="button" className="shp-iconbtn shp-iconbtn--border" onClick={start} aria-label={`Upravit: ${label}`}>
                  <Pencil size={15} aria-hidden="true" />
                </button>
              )}
            </span>
            {error && <p role="alert" className="shp-error" style={{ flexBasis: '100%', margin: 0 }}>{error}</p>}
          </div>
        )}
      </dd>
    </div>
  );
};

export default EditableField;
